/**
 * Web Push enrolment, browser side.
 *
 * The rule this module exists to hold: **never call `Notification.requestPermission()`
 * on page load.** A permission prompt that appears before anyone has asked for
 * anything is denied by reflex, and on Chrome and Safari a denial is close to
 * permanent — the person has to dig into site settings to undo it. So the
 * prompt is raised only from a real click on a control that says what it is
 * for, and `enablePush()` below is reachable from exactly one button.
 *
 * Everything here degrades to nothing. A browser without service workers, a
 * deployment without VAPID keys, a person who says no — all of them leave the
 * in-app notification list working exactly as before, because that list is the
 * delivery of record and this is the nudge.
 */
import { api } from './api';

export type PushSupport =
  /** Service workers, PushManager and Notification are all present. */
  | 'supported'
  /** iOS Safari before the app is added to the home screen. */
  | 'requires_install'
  /** The browser cannot do Web Push at all. */
  | 'unsupported';

export type PushState =
  | 'unsupported'
  | 'requires_install'
  /** The deployment has no VAPID keys; there is nothing to enrol into. */
  | 'unconfigured'
  | 'denied'
  | 'enabled'
  | 'disabled';

/**
 * iOS supports Web Push only for a site the person has added to their home
 * screen — in a plain Safari tab the API exists but subscription fails. Showing
 * an "Enable" button there produces an error the person cannot act on, so this
 * is detected up front and turned into the instruction that actually helps.
 */
export function detectSupport(): PushSupport {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return iOS ? 'requires_install' : 'unsupported';
  }
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (iOS && !standalone) return 'requires_install';
  return 'supported';
}

/**
 * VAPID keys are URL-safe base64; `subscribe` wants raw bytes.
 *
 * The view is built over an explicit `ArrayBuffer` rather than `new
 * Uint8Array(length)` so its type is `Uint8Array<ArrayBuffer>`. Since TS 5.7
 * the plain form widens to `ArrayBufferLike`, which includes `SharedArrayBuffer`
 * and is therefore not assignable to `applicationServerKey`.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing) return existing;
  return navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

/**
 * Install the service worker on load, independently of push permission.
 *
 * Registering is silent — it raises no prompt and asks for nothing. Deferring
 * it until someone enables push would mean the app has no worker most of the
 * time, and two things depend on one being installed: a browser will not offer
 * to install a PWA without one, and `pushsubscriptionchange` — the event that
 * repairs a subscription the push service has rotated — can only be handled by
 * a worker that is already there when it fires.
 */
export async function registerWorker(): Promise<void> {
  if (detectSupport() === 'unsupported') return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  await registration().catch(() => undefined);
}

/**
 * What the toggle should show on load. Read-only: this never prompts and never
 * registers anything, so it is safe to call on every mount.
 */
export async function currentState(): Promise<PushState> {
  const support = detectSupport();
  if (support !== 'supported') return support;

  const config = await api.pushConfig().catch(() => null);
  if (!config?.configured) return 'unconfigured';
  if (Notification.permission === 'denied') return 'denied';

  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  return sub ? 'enabled' : 'disabled';
}

/**
 * Raise the permission prompt and register the subscription. Call only from a
 * click.
 */
export async function enablePush(): Promise<PushState> {
  const support = detectSupport();
  if (support !== 'supported') return support;

  const config = await api.pushConfig();
  if (!config.configured || !config.publicKey) return 'unconfigured';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'disabled';

  const reg = await registration();
  // `ready` rather than the register() promise: a worker that is installed but
  // not yet active has no usable pushManager, and subscribing against it fails
  // intermittently on a first-ever enable.
  await navigator.serviceWorker.ready;

  const existing = await reg.pushManager.getSubscription();
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({
      // Required by Chrome: a push that shows nothing burns the origin's
      // budget and eventually costs the subscription.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.publicKey),
    }));

  await api.pushSubscribe(subscription.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } });
  return 'enabled';
}

/**
 * Turn it off from this device.
 *
 * The server row is revoked first. If the browser-side unsubscribe then fails,
 * the worst case is a live browser subscription the server will never send to;
 * doing it the other way round leaves the server sending to an endpoint that
 * has been dropped, which is a notification silently going nowhere.
 */
export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return 'disabled';
  await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
  return 'disabled';
}

/**
 * Re-register a subscription the browser still holds but the server may not.
 *
 * A database restore, a revoked row, or a `pushsubscriptionchange` the worker
 * missed all leave the browser believing it is subscribed while the server has
 * no endpoint for it. That state is invisible from both sides and it never
 * repairs itself, so an already-permitted device re-posts its subscription
 * once per session. The endpoint is globally unique, so a re-post is a no-op
 * when the server already has it.
 */
export async function resyncIfEnabled(): Promise<void> {
  await registerWorker();
  if (detectSupport() !== 'supported') return;
  if (Notification.permission !== 'granted') return;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api
    .pushSubscribe(sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } })
    .catch(() => undefined);
}
